import { StockListResponseType } from "@sellar-io/api-v2/types";
import { ReactElement, useState } from "react";
import { useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { BestPricePromiseDialog } from "../../../../../components/bestPricePromise/BestPricePromiseDialog";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../../components/skeleton/Skeleton";
import { TableLegacyTD } from "../../../../../components/table/TableLegacy";
import { useBasketContext, useEvents } from "../../../../../hooks";
import { useFulfillmentTypeEligibility } from "../../../../../hooks/useFulfillmentTypeEligibility";
import { useMarketStorefrontSessionId } from "../../../../../hooks/useMarketStorefrontSessionTracking";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { getProductStockToDisplay } from "../../../helpers";
import { useStorefrontFilterStore } from "../../../StorefrontFilterStore";
import { useStockListContext } from "../../../useStockListContext";
import { ItemQuantity } from "../../ItemQuantity";
import { StorefrontTableCategoryDialogContainer } from "./Dialog/StorefrontTableCategoryDialogContainer";
import { StorefrontTableProductRow } from "./StorefrontTableProductRow";
import { CheckoutSelectedFulfillmentType } from "../../..";

export const StorefrontTableProductRowContainer = ({
  hasDefaultPriceTier,
  item,
  child,
  categoryContainsPreOrderProduct,
}: {
  hasDefaultPriceTier: boolean;
  item: StockListResponseType[number];
  child: StockListResponseType[number]["Products"][number];
  categoryContainsPreOrderProduct?: boolean;
}): ReactElement => {
  const { sessionId } = useMarketStorefrontSessionId();
  const {
    supplierQuery,
    connection,
    submittedOrder,
    supplierStorefront,
    isStorefrontLoading,
    checkoutSelectedFulfillmentType,
    courierFulfillmentType,
    setCheckoutSelectedFulfillmentType,
  } = useStockListContext();

  const [{ order }] = useBasketContext();

  if (!supplierQuery.data) throw new Error("Cannot find supplier");

  const { isEligibleForDirect } = useFulfillmentTypeEligibility({
    connection: {
      fulfillmentType: connection?.fulfillmentType ?? null,
    },
    directDeliveryArea: supplierStorefront?.directDeliveryArea,
    sdDeliveryArea: supplierStorefront?.sdDeliveryArea,
    allowCollectionOnCheckout: supplierQuery.data.allowCollectionOnCheckout,
  });

  const { business } = useSellarCurrentBusiness();

  const { selectedTags, searchValue } = useStorefrontFilterStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      searchValue: state.searchValue,
    })),
  );
  const { track } = useEvents();
  const [categoryDialogActive, setCategoryDialogActive] = useState(false);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");

  const orderFlowToken = searchParams.get("orderFlowToken");

  const navigateToItemPage = () => {
    track("Product Category Clicked", {
      categoryId: item.id,
      categoryName: item.name,
      categoryType: item.kind,
      search: searchValue,
      filters: selectedTags,
      supplier: supplierQuery.data?.urlId,
      containsPreOrderProductItem: categoryContainsPreOrderProduct,
      sessionId,
    });
    setCategoryDialogActive(true);
  };

  const hasPricing = business?.Retailer && (connection || hasDefaultPriceTier);
  const hasStorefrontLinkWithPricing = storefrontLink && child.ProductPriceTier;
  const hasOrderFlowToken =
    !!orderFlowToken && (connection || hasDefaultPriceTier);

  const getGetSelectedFullfilmentType = () => {
    if (checkoutSelectedFulfillmentType) {
      return checkoutSelectedFulfillmentType;
    }

    if (connection?.fulfillmentType) {
      return connection.fulfillmentType;
    }

    return isEligibleForDirect ? "direct" : "courier";
  };

  const handleFulfillmentTypeChange = (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => {
    setCheckoutSelectedFulfillmentType(fulfillmentType);
  };

  return (
    <>
      <StorefrontTableProductRow
        item={item}
        child={child}
        onClick={navigateToItemPage}
        supplier={supplierQuery.data}
        stock={getProductStockToDisplay(
          { id: child.id, availableStock: child.availableStock },
          order,
          submittedOrder,
        )}
        fulfillmentTypeContainerDeliveryCharges={
          supplierStorefront?.FulfillmentType
            .FulfillmentTypeContainerDeliveryCharges
        }
        selectedFulfillmentType={getGetSelectedFullfilmentType()}
        courierFulfillmentType={courierFulfillmentType}
        supplierStorefront={supplierStorefront ?? undefined}
        onFulfillmentTypeChange={handleFulfillmentTypeChange}
        previewDisabled={categoryDialogActive || bestPricePromiseDialogActive}
      >
        <TableLegacyTD>
          {isStorefrontLoading ? (
            <SkeletonContainer>
              <SkeletonText className={"h-[25px] w-[120px] py-2"} />
            </SkeletonContainer>
          ) : (
            <>
              {hasPricing ||
              hasStorefrontLinkWithPricing ||
              hasOrderFlowToken ? (
                <div className="py-2">
                  <ItemQuantity
                    productItem={child}
                    showMaxQuantity={true}
                    selectedFulfillmentType={getGetSelectedFullfilmentType()}
                  />
                </div>
              ) : null}
            </>
          )}
        </TableLegacyTD>
      </StorefrontTableProductRow>
      <StorefrontTableCategoryDialogContainer
        productId={item.id}
        active={categoryDialogActive}
        setBestPricePromiseDialogActive={() => {
          setCategoryDialogActive(false);
          setBestPricePromiseDialogActive(true);
        }}
        onDismiss={() => {
          setCategoryDialogActive(false);
        }}
      />
      <BestPricePromiseDialog
        active={bestPricePromiseDialogActive}
        onDismiss={() => setBestPricePromiseDialogActive(false)}
        buyerName={business?.name}
        supplierName={supplierQuery.data.Business.name}
        productName={item.name}
      />
    </>
  );
};
